import { Button, DEFAULT_LANGUAGE, DEFAULT_WAKE_WORD } from '@cibli/shared';
import { useMicdropState } from '@micdrop/react';
import { Bot, FileText, Pause, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AnimatedAssistantState from '@/features/common/components/AnimatedAssistantState';
import AnimatedMessage from '@/features/common/components/AnimatedMessage';
import MainLayout from '@/features/common/components/MainLayout';
import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { cn } from '@/lib/utils';
import api from '@/services/api';

import CallStatusCircle from '../components/CallStatusCircle';
import { useInterviewFlowContext } from '../contexts/interview-flow.context';
import { useLocaleWakeWord } from '../hooks/useLocaleWakeWord.ts';
import { pauseInterview, resumeAfterPause } from '../pause.helper';

type SimulationScriptDto = {
  id: string;
  name: string;
  role: string;
  locale: string;
  enabled: boolean;
};

interface Props {
  onCancel: () => void;
}

export default function InterviewConversationScreen({ onCancel }: Props) {
  const { t, i18n } = useTranslation();
  const { isPaused, conversation, isListening, isUserSpeaking } = useMicdropState();
  const { lastMessage, isCVGenerating, completeDevInterview, restartWithSimulation } =
    useInterviewFlowContext();
  const { interview } = useInterviewContext();
  const { isDevNoInterviewEnabled, isSimulationModeEnabled } = useBooth();
  const { data: wakeWord } = useLocaleWakeWord();

  const [showPicker, setShowPicker] = useState(false);
  const [scripts, setScripts] = useState<SimulationScriptDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!showPicker) return;
    setLoading(true);
    api
      .get<SimulationScriptDto[]>(`/simulation-scripts?locale=${i18n.language}`)
      .then(res => setScripts(res.filter(s => s.enabled)))
      .catch(() => setScripts([]))
      .finally(() => setLoading(false));
  }, [showPicker, i18n.language]);

  const handleSelectScript = (scriptId: string) => {
    setShowPicker(false);
    restartWithSimulation(scriptId);
  };

  const getHelloMessage = () => {
    if (conversation.length === 0) {
      const isDefaultLanguage = i18n.language === DEFAULT_LANGUAGE;
      const targetWakeWord = wakeWord || DEFAULT_WAKE_WORD;

      if (isDefaultLanguage) {
        return t('Interview.Hello.title', { wakeWord: DEFAULT_WAKE_WORD });
      }

      return (
        <>
          {t('Interview.Hello.sayWakeWord', { wakeWord: targetWakeWord })}
          <br />
          <span className="text-sm opacity-80">
            {t('Interview.Hello.orSayWakeWord', { wakeWord: DEFAULT_WAKE_WORD })}
          </span>
        </>
      );
    }

    return '';
  };

  const message = isListening || isUserSpeaking ? lastMessage || getHelloMessage() : '';

  const handleCallPauseToggle = () => {
    if (isPaused) {
      if (interview) resumeAfterPause(interview.id);
    } else {
      pauseInterview();
    }
  };

  const showDevButtons = isDevNoInterviewEnabled || isSimulationModeEnabled;

  return (
    <MainLayout onClose={onCancel}>
      {/* Persona picker overlay */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-1 text-lg font-bold">
              {t('simulation.select_persona_title', { defaultValue: 'Choisir un persona' })}
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              {t('simulation.select_persona_description', {
                defaultValue: 'Sélectionnez un profil pour démarrer la simulation',
              })}
            </p>
            {loading ? (
              <p className="py-4 text-center text-gray-400">{t('common.loading')}</p>
            ) : scripts.length === 0 ? (
              <p className="py-4 text-center text-gray-400">
                {t('simulation.no_scripts_available', {
                  defaultValue: 'Aucun script disponible',
                })}
              </p>
            ) : (
              <div className="space-y-3">
                {scripts.map(script => (
                  <button
                    key={script.id}
                    onClick={() => handleSelectScript(script.id)}
                    className="w-full rounded-xl border-2 border-gray-200 bg-white p-4 text-left transition-all hover:border-emerald-500 hover:shadow-md"
                  >
                    <div className="text-sm font-semibold text-emerald-600">{script.role}</div>
                    <div className="text-base font-semibold">{script.name}</div>
                    <div className="text-xs text-gray-400">{script.locale}</div>
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowPicker(false)}
              className="mt-4 w-full rounded-lg border border-gray-200 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              {t('common.cancel', { defaultValue: 'Annuler' })}
            </button>
          </div>
        </div>
      )}

      {/* Dev buttons */}
      {showDevButtons && (
        <div className="flex justify-end gap-2 mb-4">
          {isDevNoInterviewEnabled && completeDevInterview && (
            <Button variant="outline" onClick={completeDevInterview}>
              <FileText className="mr-2 h-4 w-4" />
              {t('Interview.dev.sendScript', {
                defaultValue: "Envoyer les reponses d'interview (texte)",
              })}
            </Button>
          )}
          {isSimulationModeEnabled && (
            <Button variant="outline" onClick={() => setShowPicker(true)}>
              <Bot className="mr-2 h-4 w-4" />
              {t('simulation.select_persona_title', {
                defaultValue: "Imitation d'entretien",
              })}
            </Button>
          )}
        </div>
      )}

      <CallStatusCircle size={200} />
      <AnimatedAssistantState
        customStates={[
          {
            condition: isCVGenerating,
            message: t('Interview.state.cv_generating'),
          },
        ]}
      />

      {!isCVGenerating && (
        <div className="flex flex-col items-center gap-1 mt-8">
          <Button
            onClick={handleCallPauseToggle}
            size="icon"
            className={cn(
              'w-14 h-14',
              isPaused && 'bg-primary text-white hover:bg-primary/80',
              !isPaused && 'bg-white text-black hover:bg-white/80',
            )}
          >
            {isPaused ? (
              <Play className="min-w-8 min-h-8" />
            ) : (
              <Pause className="min-w-8 min-h-8" />
            )}
          </Button>
          <span className="text-xs text-muted-foreground">
            {isPaused ? t('Interview.state.paused') : t('Interview.pause_button_hint')}
          </span>
        </div>
      )}

      <div className="flex flex-col justify-center fixed z-[-1] top-0 left-0 right-0 h-1/2">
        <AnimatedMessage message={message} />
      </div>
    </MainLayout>
  );
}
